// The augmentation settings on the Train page (screens/train/AugmentPanel.tsx). The presets themselves come from
// the server (GET /projects/{id}/train/augment-presets, insiso_core/augment.py), so there is one definition of them.
import type { AugmentConfig } from "../api/ml";

export const PRESET_ORDER = ["off", "light", "default", "heavy"] as const;
export const PRESET_LABEL: Record<string, string> = {
  off: "Off", light: "Light", default: "Default (recommended)", heavy: "Heavy", custom: "Custom",
};

/** One row of the settings: what it does, in words, and its range. */
export interface AugmentField { key: keyof AugmentConfig; label: string; help: string; max: number; unit: "chance" | "strength" | "shrink" }
export const AUGMENT_FIELDS: AugmentField[] = [
  { key: "fliplr", label: "Flip left–right", help: "Mirror the image. Safe for almost everything except text and left/right-specific parts.", max: 1, unit: "chance" },
  { key: "flipud", label: "Flip top–bottom", help: "Mirror upside down. Good for top-down views (conveyors, trays), wrong for scenes with a clear up.", max: 1, unit: "chance" },
  { key: "rotate90", label: "Rotate 90° / 180° / 270°", help: "Turn the image. Good when objects arrive at any angle. Only quarter turns: any other angle would need boxes bigger than the objects.", max: 1, unit: "chance" },
  { key: "colour", label: "Colour and brightness", help: "Vary hue, saturation and brightness, for changing light. 100% is the long-standing amount.", max: 2, unit: "strength" },
  { key: "zoom_out", label: "Zoom out", help: "Shrink the image inside the frame (applied to half the images), so objects also appear smaller, as from further away.", max: 0.6, unit: "shrink" },
  { key: "mosaic", label: "Mosaic", help: "Combine four training images into one. Shows objects in new surroundings and at smaller sizes.", max: 1, unit: "chance" },
  { key: "blur", label: "Blur", help: "Soften the image, for motion or slightly out-of-focus cameras.", max: 1, unit: "chance" },
  { key: "noise", label: "Noise", help: "Add sensor-like grain, for low light or cheap cameras.", max: 1, unit: "chance" },
];

/** "50% of images", "strength 100%", "up to 40% smaller", or "off". */
export function describeValue(f: AugmentField, v: number): string {
  if (v <= 0) return "off";
  const pct = Math.round(v * 100);
  return f.unit === "chance" ? `${pct}% of images` : f.unit === "strength" ? `strength ${pct}%` : `up to ${pct}% smaller`;
}

/** The preset these settings are (comparing only `fields`), or "custom". */
export function presetOf(cfg: AugmentConfig, presets: Record<string, AugmentConfig>, fields: AugmentField[] = AUGMENT_FIELDS): string {
  const same = (a: AugmentConfig, b: AugmentConfig) => fields.every((f) => Math.abs(a[f.key] - b[f.key]) < 1e-6);
  return PRESET_ORDER.find((name) => presets[name] && same(cfg, presets[name])) ?? "custom";
}

/** What training did before these settings existed; used until the server's presets arrive. */
export const NO_RANGES = { rotate_min: 0, rotate_max: 0, brightness_min: 0, brightness_max: 0, contrast_min: 0, contrast_max: 0, zoom_min: 0, zoom_max: 0, hue_min: 0, hue_max: 0 };
export const DEFAULT_AUGMENT: AugmentConfig = { mosaic: 0.5, fliplr: 0.5, flipud: 0, rotate90: 0, colour: 1, zoom_out: 0, blur: 0, noise: 0, ...NO_RANGES };

// ── The Augment page: stored copies ──────────────────────────────────────────────────────────────────────────────
// Each option is a checkbox; one that is ticked applies to every copy. Rotation, brightness, contrast, zoom and hue take
// a range, and each copy draws one value between its two ends. Mosaic, colour jitter, quarter turns and the shrink-only
// zoom belong to training and are never set here (the page sends them as 0).

export type CheckKey = "fliplr" | "flipud" | "blur" | "noise";
export const CHECK_OPTIONS: { key: CheckKey; label: string; help: string }[] = [
  { key: "fliplr", label: "Flip left–right", help: "Mirror every copy. Safe for almost everything except text and left/right-specific parts." },
  { key: "flipud", label: "Flip top–bottom", help: "Mirror every copy upside down. Good for top-down views (conveyors, trays), wrong for scenes with a clear up." },
  { key: "blur", label: "Blur", help: "Soften every copy, for motion or slightly out-of-focus cameras." },
  { key: "noise", label: "Noise", help: "Add sensor-like grain to every copy, for low light or cheap cameras." },
];

export type RangeId = "rotate" | "brightness" | "contrast" | "zoom" | "hue";
export interface RangeOption {
  id: RangeId; label: string; help: string;
  /** The slider's ends, in the units shown. */
  min: number; max: number; step: number; unit: "°" | "%";
  /** How many shown units one stored unit is (the server keeps percentages as -1…+1). */
  scale: number;
  /** What ticking the box sets, in shown units. */
  start: [number, number];
  /** Words for the ends of the slider. */
  low?: string; high?: string;
  /** Shown under the settings while the option is on. */
  warning?: string;
}
export const RANGE_OPTIONS: RangeOption[] = [
  { id: "rotate", label: "Rotation", min: -180, max: 180, step: 1, unit: "°", scale: 1, start: [-30, 30],
    help: "Turn each copy by a random angle in this range (clockwise is positive). Boxes turn with the picture; one pushed past the edge becomes the upright box around what is left." },
  { id: "brightness", label: "Brightness", min: -100, max: 100, step: 1, unit: "%", scale: 100, start: [-30, 30],
    help: "Make each copy darker or lighter by a random amount in this range." },
  { id: "contrast", label: "Contrast", min: -100, max: 100, step: 1, unit: "%", scale: 100, start: [-30, 30],
    help: "Make each copy flatter or punchier by a random amount in this range." },
  { id: "zoom", label: "Zoom out / in", min: -100, max: 100, step: 1, unit: "%", scale: 100, start: [-30, 30], low: "out", high: "in",
    help: "Each copy is scaled by a random amount in this range: −100% is half the size, +100% double. Zoomed in, objects cut off by the edge are clipped, or left out when most of them is gone." },
  // Customer feedback, Oliver (2026-10-24): "Please add Hue to Augment".
  { id: "hue", label: "Hue", min: -180, max: 180, step: 1, unit: "°", scale: 1, start: [-20, 20],
    help: "Turn every colour round the colour wheel by a random angle in this range: +30° makes red look orange, 180° makes it cyan. Greys stay grey.",
    warning: "Hue changes what colour things are. Keep the range small if your classes differ only by colour (a red part and a blue part): a large turn can make one look like the other." },
];

type RangeKeys = `${RangeId}_min` | `${RangeId}_max`;
export const rangeOf = (cfg: AugmentConfig, o: RangeOption): [number, number] =>
  [cfg[`${o.id}_min` as RangeKeys] * o.scale, cfg[`${o.id}_max` as RangeKeys] * o.scale];
export const withRange = (cfg: AugmentConfig, o: RangeOption, lo: number, hi: number): AugmentConfig =>
  ({ ...cfg, [`${o.id}_min`]: lo / o.scale, [`${o.id}_max`]: hi / o.scale });
export const rangeOn = (cfg: AugmentConfig, o: RangeOption): boolean => { const [lo, hi] = rangeOf(cfg, o); return lo !== 0 || hi !== 0; };

/** The signed text of a value: "+25%", "-30°", "0%". */
export const signed = (v: number, unit: string): string => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))}${unit}`;
export const rangeText = (o: RangeOption, lo: number, hi: number): string => `${signed(lo, o.unit)} to ${signed(hi, o.unit)}`;

/** How many options are ticked. */
export function optionsOn(cfg: AugmentConfig): number {
  return CHECK_OPTIONS.filter((o) => cfg[o.key] > 0).length + RANGE_OPTIONS.filter((o) => rangeOn(cfg, o)).length;
}

const STORED_OFF: AugmentConfig = { ...DEFAULT_AUGMENT, mosaic: 0, fliplr: 0, colour: 0 };
/** Presets for the Augment page, in its own settings (the Train page's presets, from the server, are a different vocabulary). */
export const STORED_PRESETS: Record<string, AugmentConfig> = {
  off: STORED_OFF,
  light: { ...STORED_OFF, fliplr: 1, brightness_min: -0.2, brightness_max: 0.2, contrast_min: -0.2, contrast_max: 0.2 },
  default: { ...STORED_OFF, fliplr: 1, rotate_min: -15, rotate_max: 15, brightness_min: -0.3, brightness_max: 0.3, contrast_min: -0.3, contrast_max: 0.3 },
  heavy: { ...STORED_OFF, fliplr: 1, flipud: 1, blur: 1, noise: 1,
    rotate_min: -45, rotate_max: 45, brightness_min: -0.5, brightness_max: 0.5, contrast_min: -0.5, contrast_max: 0.5, zoom_min: -0.3, zoom_max: 0.3 },
};

/** The Augment page preset these settings are, or "custom". */
export function storedPresetOf(cfg: AugmentConfig): string {
  const keys = Object.keys(STORED_OFF) as (keyof AugmentConfig)[];
  return PRESET_ORDER.find((n) => keys.every((k) => Math.abs((cfg[k] ?? 0) - (STORED_PRESETS[n][k] ?? 0)) < 1e-6)) ?? "custom";
}
